import type { RobotStatus } from '../api/types';
import { toast } from '../components/toast';
import { t } from '../i18n';
import { useLive } from '../live/store';
import { sendCmd } from './robot';

export type LocState = 'located' | 'locating' | 'not_located' | 'blocked_charging';
export interface Localization {
  state: LocState;
  lastError?: string | null;
  lastTryAt?: string | null;
  /** false when the robot app does not report status.localization (older app): state is derived. */
  reported: boolean;
}

/** status.localization, or a fallback derived from location/located for older robot apps. */
export function localizationOf(st: RobotStatus | null | undefined): Localization {
  const l = st?.localization as { state?: LocState; lastError?: string | null; lastTryAt?: string | null } | undefined;
  if (l?.state) return { state: l.state, lastError: l.lastError, lastTryAt: l.lastTryAt, reported: true };
  return { state: st?.location && st.located !== false ? 'located' : 'not_located', reported: false };
}

/** Autolocate setting as reported by the robot (status.localization.auto; older: status.autoLocate / status.settings.autoLocate). */
export function autoLocateOf(st: RobotStatus | null | undefined): boolean | undefined {
  const s = st as { autoLocate?: unknown; settings?: { autoLocate?: unknown }; localization?: { auto?: unknown } } | null | undefined;
  const v = s?.localization?.auto ?? s?.autoLocate ?? s?.settings?.autoLocate;
  return typeof v === 'boolean' ? v : undefined;
}

/** Leave the charger: `undock {distance:0}` when not docked (just charging), `undock {}` when docked. */
export function leaveCharger(serial: string) {
  const st = useLive.getState().robots[serial]?.status;
  return sendCmd(serial, 'undock', st?.docked ? {} : { distance: 0 });
}

/**
 * Localization needs the robot off the charger. Returns true (and shows a toast with a "Leave charger" action)
 * when the robot is charging, so the caller must not send locate.
 */
export function blockedByCharging(serial: string): boolean {
  const r = useLive.getState().robots[serial];
  if (!r?.status?.charging) return false;
  toast(t('loc.offChargerFirst', { name: r.name }), 'err', { label: t('loc.leaveCharger'), onClick: () => void leaveCharger(serial) });
  return true;
}

/** `locate {}` without a hint (the robot searches its pose itself). */
export async function locateAuto(serial: string) {
  if (blockedByCharging(serial)) return null;
  return sendCmd(serial, 'locate', {}, t('loc.auto'));
}

export function setAutoLocate(serial: string, on: boolean) {
  return sendCmd(serial, 'setSettings', { autoLocate: on });
}
